
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Shield, AlertTriangle, Clock, CheckCircle, Users, DollarSign } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const Rules = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-yellow-500 to-yellow-300 py-16">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Rules & Regulations</h1>
          <p className="text-xl text-gray-700">Important information for all GoldenTicketLucky players</p>
        </div>
      </section>

      {/* Rules Content */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <Tabs defaultValue="general" className="w-full">
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-4">
              <TabsTrigger value="general">General Rules</TabsTrigger>
              <TabsTrigger value="eligibility">Eligibility</TabsTrigger>
              <TabsTrigger value="prizes">Prize Claims</TabsTrigger>
              <TabsTrigger value="responsible">Responsible Gaming</TabsTrigger>
            </TabsList>
            
            <TabsContent value="general">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="w-6 h-6 text-yellow-600" />
                      Game Rules
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">Ticket Purchase</h4>
                      <ul className="list-disc list-inside text-gray-700 space-y-1">
                        <li>Tickets must be purchased before draw closing time</li>
                        <li>All sales are final - no refunds or exchanges</li>
                        <li>Players must be 18 years or older</li>
                        <li>Maximum of 50 games per transaction</li>
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">Number Selection</h4>
                      <ul className="list-disc list-inside text-gray-700 space-y-1">
                        <li>Choose your own numbers or use Quick Pick</li>
                        <li>Numbers must be within the specified range</li>
                        <li>Duplicate numbers are not allowed</li>
                        <li>System entries allow multiple number combinations</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="w-6 h-6 text-yellow-600" />
                      Draw Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">Draw Times</h4>
                      <ul className="list-disc list-inside text-gray-700 space-y-1">
                        <li>PowerBall: Thursday 8:00 PM AEDT</li>
                        <li>Oz Lotto: Tuesday 8:00 PM AEDT</li>
                        <li>Saturday Lotto: Saturday 8:00 PM AEDT</li>
                        <li>Sales close 30 minutes before draw</li>
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">Draw Process</h4>
                      <ul className="list-disc list-inside text-gray-700 space-y-1">
                        <li>Draws are conducted using certified equipment</li>
                        <li>Independent auditors oversee all draws</li>
                        <li>Results are published immediately after draws</li>
                        <li>All draws are recorded and archived</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
            
            <TabsContent value="eligibility">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-6 h-6 text-yellow-600" />
                    Player Eligibility Requirements
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3">Age Requirements</h4>
                    <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                      <p className="text-gray-800">
                        <strong>You must be 18 years or older</strong> to purchase lottery tickets or claim prizes. 
                        Age verification is required for all players.
                      </p>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3">Residency Requirements</h4>
                    <ul className="list-disc list-inside text-gray-700 space-y-2">
                      <li>Must be an Australian resident or citizen</li>
                      <li>Valid Australian address required for registration</li>
                      <li>International players must comply with local laws</li>
                      <li>Some jurisdictions may restrict online lottery participation</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3">Account Requirements</h4>
                    <ul className="list-disc list-inside text-gray-700 space-y-2">
                      <li>One account per person only</li>
                      <li>Valid email address and phone number required</li>
                      <li>Identity verification may be required for large wins</li>
                      <li>Account sharing is strictly prohibited</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3">Prohibited Persons</h4>
                    <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                      <ul className="list-disc list-inside text-red-800 space-y-1">
                        <li>Persons under 18 years of age</li>
                        <li>Employees and immediate family of GoldenTicketLucky</li>
                        <li>Persons on self-exclusion lists</li>
                        <li>Persons in jurisdictions where online gambling is prohibited</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="prizes">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <DollarSign className="w-6 h-6 text-yellow-600" />
                      Prize Claims
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">Claim Periods</h4>
                      <ul className="list-disc list-inside text-gray-700 space-y-1">
                        <li>Prizes must be claimed within 12 months</li>
                        <li>Unclaimed prizes are forfeited after this period</li>
                        <li>Winners will be notified via registered contact details</li>
                        <li>It is the player's responsibility to check results</li>
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">Prize Verification</h4>
                      <ul className="list-disc list-inside text-gray-700 space-y-1">
                        <li>Identity verification required for prizes over $1,000</li>
                        <li>Original ticket or account verification needed</li>
                        <li>Tax File Number may be required for large prizes</li>
                        <li>Prizes may be subject to taxation</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle className="w-6 h-6 text-yellow-600" />
                      Payment Methods
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">Small Prizes (Under $1,000)</h4>
                      <ul className="list-disc list-inside text-gray-700 space-y-1">
                        <li>Automatic credit to account balance</li>
                        <li>Bank transfer available upon request</li>
                        <li>Usually processed within 24 hours</li>
                        <li>No additional verification required</li>
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">Large Prizes ($1,000+)</h4>
                      <ul className="list-disc list-inside text-gray-700 space-y-1">
                        <li>Bank transfer after verification</li>
                        <li>Processing time 3-5 business days</li>
                        <li>Identity and address verification required</li>
                        <li>Financial counseling services offered</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
            
            <TabsContent value="responsible">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-6 h-6 text-yellow-600" />
                    Responsible Gaming
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                    <h4 className="font-bold text-blue-900 mb-3">Our Commitment</h4>
                    <p className="text-blue-800">
                      GoldenTicketLucky is committed to promoting responsible gambling and providing a safe, 
                      enjoyable experience for all our customers. We provide tools and resources to help 
                      you stay in control of your gaming.
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3">Setting Limits</h4>
                    <ul className="list-disc list-inside text-gray-700 space-y-2">
                      <li>Set daily, weekly, or monthly spending limits</li>
                      <li>Time limits for gaming sessions</li>
                      <li>Deposit limits can be adjusted (with cooling-off periods)</li>
                      <li>Loss limits to protect your bankroll</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3">Self-Exclusion</h4>
                    <ul className="list-disc list-inside text-gray-700 space-y-2">
                      <li>Temporary exclusion periods (24 hours to 6 months)</li>
                      <li>Permanent self-exclusion available</li>
                      <li>Account closure with immediate effect</li>
                      <li>Support during exclusion periods</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3">Warning Signs</h4>
                    <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                      <p className="text-amber-800 font-medium mb-2">Seek help if you experience:</p>
                      <ul className="list-disc list-inside text-amber-800 space-y-1">
                        <li>Spending more than you can afford</li>
                        <li>Chasing losses with bigger bets</li>
                        <li>Neglecting work, family, or social responsibilities</li>
                        <li>Feeling anxious or depressed about gambling</li>
                        <li>Lying about gambling activities</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3">Get Help</h4>
                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                      <div className="space-y-3 text-green-800">
                        <p><strong>GambleAware NSW:</strong> <a href="tel:+61359294105" className="underline">+61 35 9294 105</a></p>
                        <p><strong>Gambling Help Online:</strong> <a href="https://gamblinghelponline.org.au" target="_blank" rel="noopener noreferrer" className="underline">gamblinghelponline.org.au</a></p>
                        <p><strong>BetStop National Self-Exclusion:</strong> <a href="https://betstop.gov.au" target="_blank" rel="noopener noreferrer" className="underline">betstop.gov.au</a></p>
                        <p><strong>Lifeline:</strong> <a href="tel:131114" className="underline">13 11 14</a></p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Rules;
